// ─── Demandes de changement de garde — liste + acquittement ──────
// L'autre parent approuve (acquitte) ou refuse une demande ; l'auteur peut
// annuler la sienne tant qu'elle est en attente.
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useUI, useT, PageHeader, GesteRetourEcran, BarreRecherche, CalqueChoix, LigneChoix, confirmerSuppression, frJMA } from '@mop/core';
import { traiterDemande, annulerDemande, STATUT_ATTENTE, STATUT_APPROUVE, STATUT_REFUSE } from './services/demandes';
import { GARDE_COL_DEFAUT } from './services/garde';

const GARDE_COL = GARDE_COL_DEFAUT;   // source unique : services/garde
const fmtFR = frJMA;   // JJ/MM/AAAA (Core)

export default function EcranDemandes({ calId, demandes = [], user, parents, erreurLecture, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');
  // Filtre (statut) + tri (date) : les deux boutons qui encadrent la
  // recherche, par convention app-wide (18/08, Cédric).
  const [statutFiltre, setStatutFiltre] = useState(null);   // null = toutes
  const [filtreOuvert, setFiltreOuvert] = useState(false);
  const [triAsc, setTriAsc] = useState(false);   // défaut = plus récentes d'abord
  const moi = user?.uid;
  const nomG = (g) => (g === 'parent1' ? (parents?.parent1?.nom || t('garde.parent1'))
    : g === 'parent2' ? (parents?.parent2?.nom || t('garde.parent2')) : '—');
  const gardeCol = { parent1: parents?.couleur1 || GARDE_COL.parent1, parent2: parents?.couleur2 || GARDE_COL.parent2 };

  async function agir(d, statut) {
    if (busy) return;
    setBusy(d.id);
    try { await traiterDemande({ demandeId: d.id, traiteurUid: moi, statut }); } catch (_) {}
    setBusy(null);
  }

  // Recherche libre : la DATE d'abord (« 22/08 », « 2026 », « 2026-08-22 »),
  // mais aussi les parents, l'auteur et le message — on cherche rarement en
  // sachant lequel des quatre on a en tete.
  const correspond = (d) => {
    const req = q.trim().toLowerCase();
    if (!req) return true;
    return [fmtFR(d.dateKey), d.dateKey, nomG(d.gardeActuelle), nomG(d.gardeProposee), d.demandeParNom, d.message]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(req));
  };
  const correspondStatut = (d) => !statutFiltre || d.statut === statutFiltre;
  const parDate = (a, b) => (a.dateKey < b.dateKey ? -1 : a.dateKey > b.dateKey ? 1 : 0) * (triAsc ? 1 : -1);
  const enAttente = demandes.filter(d => d.statut === STATUT_ATTENTE && correspond(d) && correspondStatut(d)).sort(parDate);
  const traiteesToutes = demandes.filter(d => d.statut !== STATUT_ATTENTE && correspond(d) && correspondStatut(d)).sort(parDate);
  // Sans recherche on borne a 20 pour ne pas derouler des annees d'historique ;
  // DES qu'on cherche on montre tout le resultat, sinon le filtre mentirait
  // (« pas trouve » alors que la ligne existe au-dela du 20e rang).
  const chercheActive = !!q.trim() || !!statutFiltre;
  const traitees = chercheActive ? traiteesToutes : traiteesToutes.slice(0, 20);
  const tronque = !chercheActive && traiteesToutes.length > traitees.length;
  const aucunResultat = chercheActive && !enAttente.length && !traitees.length;

  const carte = (d) => {
    const monAppro = d.approbations?.[moi];
    const doitAcquitter = d.statut === STATUT_ATTENTE && monAppro?.requis && monAppro.statut === STATUT_ATTENTE && d.demandePar !== moi;
    const estMienne = d.demandePar === moi;
    const statutTxt = d.statut === STATUT_APPROUVE ? (d.auto ? t('demandes.statutAppliquee') : t('demandes.statutAcquittee'))
      : d.statut === STATUT_REFUSE ? t('demandes.statutRefusee') : t('demandes.statutAttente');
    const statutCol = d.statut === STATUT_APPROUVE ? '#2E7D32' : d.statut === STATUT_REFUSE ? '#C62828' : UI.muted;
    return (
      <View key={d.id} style={s.carte}>
        <View style={s.tete}>
          <View style={{ flex: 1 }}>
            <Text style={s.date}>🧷 {fmtFR(d.dateKey)}</Text>
            <Text style={s.par} numberOfLines={1}>{t('demandes.proposePar', { nom: d.demandeParNom || t('demandes.unParent') })}{estMienne ? t('demandes.vousSuffixe') : ''}</Text>
          </View>
          <Text style={[s.statut, { color: statutCol }]}>{statutTxt}</Text>
        </View>
        <View style={s.chgLigne}>
          <View style={[s.pastilleG, { backgroundColor: (gardeCol[d.gardeActuelle] || UI.faint) + '22', borderColor: gardeCol[d.gardeActuelle] || UI.border }]}>
            <Text style={[s.pastilleGTxt, { color: gardeCol[d.gardeActuelle] || UI.muted }]} numberOfLines={1}>{nomG(d.gardeActuelle)}</Text>
          </View>
          <Text style={s.fleche}>→</Text>
          <View style={[s.pastilleG, { backgroundColor: (gardeCol[d.gardeProposee] || UI.faint) + '22', borderColor: gardeCol[d.gardeProposee] || UI.border }]}>
            <Text style={[s.pastilleGTxt, { color: gardeCol[d.gardeProposee] || UI.muted }]} numberOfLines={1}>{nomG(d.gardeProposee)}</Text>
          </View>
        </View>
        {!!d.message && <Text style={s.msg}>« {d.message} »</Text>}

        {doitAcquitter && (
          <View style={s.actions}>
            <TouchableOpacity style={[s.btn, s.btnRefus]} disabled={busy === d.id} onPress={() => agir(d, STATUT_REFUSE)}>
              <Text style={[s.btnTxt, { color: '#C62828' }]}>{t('demandes.refuser')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.btn, s.btnAppr]} disabled={busy === d.id} onPress={() => agir(d, STATUT_APPROUVE)}>
              <Text style={[s.btnTxt, { color: '#fff' }]}>{t('demandes.acquitter')}</Text>
            </TouchableOpacity>
          </View>
        )}
        {estMienne && d.statut === STATUT_ATTENTE && (
          <TouchableOpacity style={s.annuler}
            onPress={() => confirmerSuppression(t('demandes.annulerDemande'), t('demandes.changementRetireMsg', { date: fmtFR(d.dateKey) }), () => annulerDemande(d.id).catch(() => {}))}>
            <Text style={s.annulerTxt}>{t('demandes.annulerMaDemande')}</Text>
          </TouchableOpacity>
        )}
        {/* Demande de l'autre parent qu'on ne peut pas acquitter → on peut la RETIRER
            (co-gestion du calendrier), pour ne pas rester bloqué sur une demande en attente. */}
        {!estMienne && !doitAcquitter && d.statut === STATUT_ATTENTE && (
          <TouchableOpacity style={s.annuler}
            onPress={() => confirmerSuppression(t('demandes.retirerLaDemande'), t('demandes.retirerLaDemandeMsg', { date: fmtFR(d.dateKey) }), () => annulerDemande(d.id).catch(() => {}))}>
            <Text style={s.annulerTxt}>{t('demandes.retirerLaDemande')}</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('demandes.titreGarde')} iconeTitre="🧷" onRetour={onFermer} />
      <ScrollView contentContainerStyle={s.corps}>
      {/* La cause AVANT la liste : une liste vide par refus de lecture n'est
          pas une liste vide (même patron que Contacts/Journal/messagerie). */}
      {!!erreurLecture && (
        <View style={s.bandeau}>
          <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureDemandes')}</Text>
          <Text style={s.bandeauDet}>{erreurLecture}</Text>
        </View>
      )}
      {erreurLecture ? null : demandes.length === 0 ? (
        <Text style={s.vide}>{t('demandes.videGarde')}</Text>
      ) : (
        <>
          {/* Barre de recherche en haut, comme sur les autres listes — filtre
              (statut) à gauche, tri (date) à droite, par convention app-wide.
              Masquee tant que la liste tient a l'ecran : un champ vide pour
              trois lignes est du bruit. */}
          {demandes.length > 3 && (
            <BarreRecherche valeur={q} onChange={setQ} placeholder={t('demandes.rechercher')}
              nbFiltres={statutFiltre ? 1 : 0} onFiltres={() => setFiltreOuvert(true)}
              triActif={triAsc} onTri={() => setTriAsc(x => !x)} />
          )}
          {aucunResultat && <Text style={s.vide}>{t('demandes.aucunResultat')}</Text>}
          {enAttente.length > 0 && <Text style={s.section}>{t('demandes.sectionEnAttente', { n: enAttente.length })}</Text>}
          {enAttente.map(carte)}
          {traitees.length > 0 && <Text style={s.section}>{t('famille.historiqueLabel')}</Text>}
          {traitees.map(carte)}
          {/* Le plafond etait silencieux : on dit ce qui n'est pas montre, et
              comment atteindre le reste. */}
          {tronque && <Text style={s.tronque}>{t('demandes.historiqueTronque', { n: traitees.length, total: traiteesToutes.length })}</Text>}
        </>
      )}
      </ScrollView>
      <CalqueChoix visible={filtreOuvert} titre={t('demandes.filtreStatutTitre')} emoji="🔽"
        onFermer={() => setFiltreOuvert(false)}>
        {[
          [null, t('demandes.filtreToutes')],
          [STATUT_ATTENTE, t('demandes.statutAttente')],
          [STATUT_APPROUVE, t('demandes.statutAppliquee')],
          [STATUT_REFUSE, t('demandes.statutRefusee')],
        ].map(([val, label]) => (
          <LigneChoix key={String(val)} libelle={statutFiltre === val ? `✓ ${label}` : label}
            onPress={() => { setStatutFiltre(val); setFiltreOuvert(false); }} />
        ))}
      </CalqueChoix>
    </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 19, fontStyle: 'italic' },
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
  tronque: { fontSize: 11.5, color: UI.faint, fontStyle: 'italic', textAlign: 'center', marginTop: 8 },
  section: { fontSize: 12, fontWeight: '800', color: UI.muted, marginTop: 12, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.4 },
  // Compacté le 18/08 (Cédric) : moins de remplissage/marges, « proposé par »
  // fusionné dans l'en-tête (avec la date) plutôt qu'en ligne à part — une
  // carte tient sur moins de hauteur sans perdre d'information.
  carte: { borderWidth: 1, borderColor: UI.border, borderRadius: 12, backgroundColor: UI.card, padding: 10, marginBottom: 7 },
  tete: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  date: { fontSize: 14, fontWeight: '800', color: UI.ink },
  statut: { fontSize: 12, fontWeight: '700' },
  chgLigne: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 },
  pastilleG: { flex: 1, borderWidth: 1.5, borderRadius: 9, paddingVertical: 5, paddingHorizontal: 8, alignItems: 'center' },
  pastilleGTxt: { fontSize: 12.5, fontWeight: '800' },
  fleche: { fontSize: 16, color: UI.muted, fontWeight: '700' },
  par: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  msg: { fontSize: 12, color: UI.ink, fontStyle: 'italic', marginTop: 3 },
  actions: { flexDirection: 'row', gap: 8, marginTop: 8 },
  btn: { flex: 1, paddingVertical: 8, borderRadius: 10, alignItems: 'center' },
  btnRefus: { backgroundColor: UI.champ, borderWidth: 1, borderColor: '#C62828' },
  btnAppr: { backgroundColor: '#2E7D32' },
  btnTxt: { fontSize: 13.5, fontWeight: '800' },
  annuler: { marginTop: 6, alignItems: 'center' },
  annulerTxt: { fontSize: 12.5, color: '#C62828', fontWeight: '700' },
});
